import "./styles.css";

import {
  mountScenePlayer,
  type ActionOutcome,
  type DiscoveriesResponse,
  type GenerationStatus,
  type OffersResponse,
  type SceneResponse,
} from "./player";

const root = document.querySelector<HTMLElement>("#app");
const scenePath = root?.dataset.scenePath;

async function postJson<T>(path: string, body?: unknown): Promise<{ ok: boolean; data: T }> {
  const response = await fetch(path, {
    method: "POST",
    headers: body === undefined ? {} : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return { ok: response.ok, data: (await response.json()) as T };
}

if (root && scenePath) {
  void mountScenePlayer(root, `${scenePath}${location.search}`, {
    loadScene: async (apiPath) => {
      const response = await fetch(apiPath, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("scene_request_failed");
      return (await response.json()) as SceneResponse;
    },
    submitAction: async (path, action) => {
      const { ok, data } = await postJson<ActionOutcome & { error?: string }>(
        `/api/scenes${path}/actions`,
        { action },
      );
      return ok ? data : { kind: "rejected", error: data.error ?? "action_invalid" };
    },
    loadDiscoveries: async () => {
      const response = await fetch("/api/me/discoveries", { headers: { Accept: "application/json" }, cache: "no-store" });
      if (!response.ok) throw new Error("discoveries_unavailable");
      return (await response.json()) as DiscoveriesResponse;
    },
    loadOffers: async () => {
      const response = await fetch("/api/offers", { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("offers_unavailable");
      return (await response.json()) as OffersResponse;
    },
    startCheckout: async (pendingActionId: string, product: string) => {
      const { ok, data } = await postJson<{ checkoutUrl: string }>(
        `/api/pending-actions/${pendingActionId}/checkout`,
        { product },
      );
      if (!ok) throw new Error("checkout_unavailable");
      return data;
    },
    requestGeneration: async (pendingActionId: string) => {
      const { ok, data } = await postJson<{ generationId: string; status: string }>(
        `/api/pending-actions/${pendingActionId}/generation`,
      );
      if (!ok) throw new Error("generation_unavailable");
      return data;
    },
    pollGeneration: async (generationId: string) => {
      const response = await fetch(`/api/generations/${generationId}`);
      if (!response.ok) throw new Error("generation_unavailable");
      return (await response.json()) as GenerationStatus;
    },
    navigate: (url) => location.assign(url),
    reportEvent: (type, detail) => {
      const body = JSON.stringify({ type, scenePath, detail });
      if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/events", new Blob([body], { type: "application/json" }));
        return;
      }
      void fetch("/api/events", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => undefined);
    },
    wait: (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)),
  });
}
